import React, { useMemo } from 'react';
import { getLocale } from 'umi';
import BubbleCommon from '@op/components/BubbleCommon';
import StickyFilterMetaItem from '@/pages/dashboard/components/NewFilter/StickyFilterMetaItem';
import {
  KPI_EVENT_FILTER_LANGUAGE_DEFAULTS,
  type IKpiEventFilterLanguage,
} from './kpiEventFilterLanguageDefaults';
import type { IChartEventSetting } from './index';

function multilineTips(text: string): JSX.Element {
  const lines = text.split('\n');
  return (
    <span>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 ? <br /> : null}
          {line}
        </React.Fragment>
      ))}
    </span>
  );
}

export interface IKpiEventStickySlotProps {
  chartEventSetting: IChartEventSetting;
  language?: Partial<IKpiEventFilterLanguage>;
}

/**
 * KPI 吸顶条末尾「显示事件」摘要：与 FilterTitle 并列，纯展示当前总开关状态。
 *
 * 与其它吸顶字段（Day / Region / Platform 等）一致：吸顶区域只做摘要展示，
 * 不响应交互修改状态；点击吸顶任意位置冒泡到 NewFilter 根 Card 触发筛选器展开，
 * 真正的「显示事件」开关由展开后的 KpiEventFilter Checkbox 控制。
 */
const KpiEventStickySlot: React.FC<IKpiEventStickySlotProps> = ({
  chartEventSetting,
  language: languageProp,
}) => {
  const mergedLanguage = useMemo(
    () => ({
      ...KPI_EVENT_FILTER_LANGUAGE_DEFAULTS,
      ...languageProp,
    }),
    [languageProp],
  );

  const isZh = getLocale() === 'zh-CN';
  const stickyLabel = isZh
    ? mergedLanguage.checkboxLabelZh
    : mergedLanguage.checkboxLabelEn;

  const showsOn =
    chartEventSetting.showEventAxis || chartEventSetting.showEventBubbles;

  const stickyValueText = showsOn
    ? isZh
      ? mergedLanguage.stickyValueOnZh ?? '显示'
      : mergedLanguage.stickyValueOnEn ?? 'Show'
    : isZh
      ? mergedLanguage.stickyValueOffZh ?? '隐藏'
      : mergedLanguage.stickyValueOffEn ?? 'Hide';

  const primaryTipsMap = useMemo(
    () => ({
      en: multilineTips(mergedLanguage.primaryTipsEn),
      zh: multilineTips(mergedLanguage.primaryTipsZh),
    }),
    [mergedLanguage.primaryTipsEn, mergedLanguage.primaryTipsZh],
  );

  return (
    <BubbleCommon showIcon={false} tipsMap={primaryTipsMap}>
      <div style={{ display: 'inline-flex', alignItems: 'center' }}>
        <StickyFilterMetaItem label={stickyLabel} value={stickyValueText} />
      </div>
    </BubbleCommon>
  );
};

export default KpiEventStickySlot;
